import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingCtas from "@/components/FloatingCtas";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import LeadPopup from "@/components/LeadPopup";

const Life = lazy(() => import("@/pages/Life"));
const Unplugged = lazy(() => import("@/pages/Unplugged"));
const Courses = lazy(() => import("@/pages/Courses"));
const PlayFirst = lazy(() => import("@/pages/PlayFirst"));
const Founder = lazy(() => import("@/pages/Founder"));
const WhatsappInquiry = lazy(() => import("@/pages/WhatsappInquiry"));
const Testimonials = lazy(() => import("@/pages/Testimonials"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Login = lazy(() => import("@/pages/Login"));
const Admin = lazy(() => import("@/pages/Admin"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const bare = location.pathname.startsWith("/admin") || location.pathname.startsWith("/login");
  return (
    <>
      {!bare && <Navbar />}
      <div className={bare ? "" : "pb-16 sm:pb-0"}>
        <Suspense fallback={null}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/life" element={<Life />} />
          <Route path="/tmh-unplugged" element={<Unplugged />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/play-first-pay-later" element={<PlayFirst />} />
          <Route path="/music-fitness-check" element={<Navigate to="/play-first-pay-later" replace />} />
          <Route path="/founder" element={<Founder />} />
          <Route path="/whatsapp-inquiry" element={<WhatsappInquiry />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<Admin />} />
          </Routes>
        </Suspense>
      </div>
      {!bare && <Footer />}
      {!bare && <FloatingCtas />}
      {!bare && <LeadPopup />}
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App font-body">
          <AppRouter />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
